import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import theme from "../../theme";
import { useEffect, useState } from "react";
import { Tooltip } from "@mui/material";
import { GetMobilityLiveList } from "../../types/types";
import { handleKeyDown } from "../../utils/util";

const blink = keyframes`  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.2;
  }
  100% {
    opacity: 1;
  }`;

const shake = keyframes`
    0%,
  100% {
    -webkit-transform: rotate(0deg);
            transform: rotate(0deg);
    -webkit-transform-origin: 50% 0;
            transform-origin: 50% 0;
  }
  10% {
    -webkit-transform: rotate(2deg);
            transform: rotate(2deg);
  }
  20%,
  40%,
  60% {
    -webkit-transform: rotate(-4deg);
            transform: rotate(-4deg);
  }
  30%,
  50%,
  70% {
    -webkit-transform: rotate(4deg);
            transform: rotate(4deg);
  }
  80% {
    -webkit-transform: rotate(-2deg);
            transform: rotate(-2deg);
  }
  90% {
    -webkit-transform: rotate(2deg);
            transform: rotate(2deg);
  }
`;

type Prop = {
  data?: GetMobilityLiveList;
};

/**
 *  전복사고 발생시 출력되는
 * 시각적 경보 컴포넌트
 *
 */
export const AccidentBlink = ({ data }: Prop) => {
  const [isActive, setIsActive] = useState(false); // 블링크 출력 조건
  const knowList = localStorage.getItem("KNOW"); // 로그인 유저가 사고 발생 인지한 데이터 리스트

  useEffect(() => {
    if (!data || !data.list) return;
    const list = data.list;

    // 전체 전복사고 이벤트 리스트 생성
    const targets = list
      .filter((el) => el.event_accident !== "정상")
      .map((el) => el.accident_header_seq);
    // console.log(targets);
    // 전복 사고 없음
    if (targets.length === 0) {
      localStorage.removeItem("KNOW");
      setIsActive(false);
    }

    // 전복 사고 있음
    if (targets.length > 0) {
      // 로컬 스토리지에 "KNOW" 키가 없음 (사고 최초 발생)
      if (!knowList) {
        setIsActive(true); // blink 컴포넌트 활성화
      }
      // 로컬 스토리지에 KNOW 키가 있음
      else if (knowList) {
        const knowArr = JSON.parse(knowList);

        // 전복 해제 체크
        const targetsSet = new Set(targets);
        // newKnowList는 로컬에 저장된 KNOW 데이터에서 전복 해제된 데이터를 제외한 배열
        const newKnowList = knowArr.filter((seq: number) =>
          targetsSet.has(seq)
        );
        const newKnowListSet = new Set(newKnowList);

        // 확인되지 않은 사고 리스트
        let unkownList: number[] = [];

        for (let seq of targets) {
          // newKnowListSet 데이터에 없는 새로운 사고 seq를 unkownList에 저장
          if (seq && !newKnowListSet.has(seq)) {
            unkownList.push(seq);
          }
        }

        // 확인되지 않은 사고 리스트가 1개 이상인 경우
        if (unkownList.length > 0) {
          setIsActive(true); // blink 컴포넌트 활성화
        }

        // 확인되지 않은 사고 리스트가 없음
        if (unkownList.length === 0) {
          setIsActive(false);
        }
      }
    }

    // console.log(targets);
  }, [data]);

  const handleClick = () => {
    if (!data || !data.list) return;
    const list = data.list;
    const targets = list
      .filter((el) => el.event_accident !== "정상")
      .map((el) => el.accident_header_seq);

    localStorage.setItem("KNOW", JSON.stringify(targets));
    setIsActive(false);
  };

  if (!isActive) return null;

  return (
    <Tooltip
      title="클릭하여 화면 알림 끄기 🚨"
      followCursor
      placement="right-start"
      PopperProps={{
        sx: {
          "& .MuiTooltip-tooltip": {
            fontSize: "16px",
            fontWeight: "700",
            animation: `${shake} 0.8s cubic-bezier(0.455, 0.030, 0.515, 0.955) infinite both`,
          },
          "& .MuiTooltip-tooltipPlacementRight": {
            backgroundColor: theme.color.pastelRed_500,
          },
        },
      }}
    >
      <Container
        role="button"
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
      ></Container>
    </Tooltip>
  );
};

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  position: absolute;
  z-index: ${theme.zindex.accidentBlink};
  background-color: ${theme.color.pastelRed_500}99;
  cursor: pointer;

  -webkit-animation: ${blink} 0.9s infinite both;
  animation: ${blink} 0.9s infinite both;
`;
